import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Shield, CheckCircle, AlertTriangle } from 'lucide-react';

export interface PermissionGridModule {
  modulecode: string;
  modulename: string;
  actions: string[];
}

interface PermissionGridProps {
  availableModules: PermissionGridModule[];
  /** modulecode -> { action: enabled } */
  modulePermissions: { [modulecode: string]: { [action: string]: boolean } };
  onToggleAction: (modulecode: string, action: string) => void;
  onToggleAllActions: (modulecode: string, actions: string[]) => void;
  isLoading?: boolean;
  disabled?: boolean;
}

/**
 * The module/action permission grid shared by the per-member override editor
 * (EditPermissions.tsx) and the workgroup role editor (RolesManagement.tsx) --
 * both are ultimately editing the same shape of data (a module -> actions map),
 * just against different backing tables (userpermissions vs rolepermissions).
 */
export function PermissionGrid({
  availableModules,
  modulePermissions,
  onToggleAction,
  onToggleAllActions,
  isLoading,
  disabled,
}: PermissionGridProps) {
  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-sm text-muted-foreground">Loading modules...</p>
      </div>
    );
  }

  if (availableModules.length === 0) {
    return (
      <div className="text-center py-8 border rounded-lg bg-muted/50">
        <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
        <p className="text-sm text-muted-foreground">No modules available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {availableModules.map((module) => {
        const currentPerms = modulePermissions[module.modulecode] || {};
        const hasAnyPermission = Object.keys(currentPerms).length > 0;
        const allEnabled = module.actions.length > 0 && module.actions.every((action) => currentPerms[action]);

        return (
          <div key={module.modulecode} className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-blue-600" />
                <h4 className="font-medium">{module.modulename}</h4>
                {hasAnyPermission && (
                  <Badge variant="secondary" className="bg-green-100 text-green-800 text-xs">
                    {Object.keys(currentPerms).length} action{Object.keys(currentPerms).length !== 1 ? 's' : ''}
                  </Badge>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onToggleAllActions(module.modulecode, module.actions)}
                className="text-xs h-7"
                disabled={disabled}
              >
                {allEnabled ? 'Deselect All' : 'Select All'}
              </Button>
            </div>

            <div className="flex flex-wrap gap-2">
              {module.actions.map((action) => {
                const isEnabled = currentPerms[action];
                return (
                  <Badge
                    key={action}
                    variant={isEnabled ? 'default' : 'outline'}
                    className={`transition-colors ${disabled ? 'opacity-60' : 'cursor-pointer'} ${
                      isEnabled ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'hover:bg-muted'
                    }`}
                    onClick={() => !disabled && onToggleAction(module.modulecode, action)}
                  >
                    {isEnabled && <CheckCircle className="w-3 h-3 mr-1" />}
                    {action}
                  </Badge>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
